/**
 * DecisionsTab — read-only log of guardrail decisions made by the live
 * engine. Never shows raw query text — only a SHA-256 hash is stored.
 *
 * The window is the shared time filter, serialised to the log's own
 * `date_from`/`date_to` (local `YYYY-MM-DD`). It applies on click, unlike the
 * text filters, which still wait for the Filter button.
 */

import React, { useEffect, useState } from 'react';
import { Box, Grid, TextField, Select, MenuItem, Button, Alert, Chip, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import DataTable, { Column } from '../../common/DataTable';
import TimeRangeFilter from '../../common/TimeRangeFilter';
import useTimeRangeParam from '../../common/useTimeRangeParam';
import { resolveRangeYmd } from '../../common/timeRange';
import { colors } from '../../../theme/designTokens';
import { quillApi, QuillApiError, GuardrailDecision, DecisionsQueryParams } from '../../../services/quillAdminService';
import { verdictColor, verdictLabel, matchTypeLabel, truncateId, DECISION_VERDICTS } from './guardrailUi';

const DEFAULT_LIMIT = 50;

const DecisionsTab: React.FC = () => {
  const [decisions, setDecisions] = useState<GuardrailDecision[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [memberId, setMemberId] = useState('');
  const [verdict, setVerdict] = useState('');
  const [category, setCategory] = useState('');
  const [range, setRange] = useTimeRangeParam({ type: '30d' }, { prefix: 'decisions' });
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);

  // Intentionally NOT memoized with useCallback: it must always close over the
  // latest filter field values when called directly from the Filter button.
  // The useEffect below re-runs it only on `offset` changes (Prev/Next).
  const load = async () => {
    setLoading(true);
    setError(null);
    const { from, to } = resolveRangeYmd(range);
    const params: DecisionsQueryParams = {
      member_id: memberId.trim() || undefined,
      verdict: verdict || undefined,
      category: category.trim() || undefined,
      date_from: from,
      date_to: to,
      limit,
      offset,
    };
    try {
      const data = await quillApi.guardrails.getDecisions(params);
      setDecisions(data);
    } catch (e) {
      setError(e instanceof QuillApiError ? e.message : 'Failed to load decisions log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, range]);

  const changeRange = (next: typeof range) => {
    setOffset(0);
    setRange(next);
  };

  const runFilter = () => {
    if (offset !== 0) {
      setOffset(0);
    } else {
      load();
    }
  };

  const columns: Column<GuardrailDecision>[] = [
    {
      id: 'occurred_at',
      label: 'When',
      render: (row) => (
        <Typography variant="body2" title={row.occurred_at}>
          {formatDistanceToNow(new Date(row.occurred_at), { addSuffix: true })}
        </Typography>
      ),
    },
    {
      id: 'quill_member_id',
      label: 'Member',
      render: (row) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }} title={row.quill_member_id}>
          {truncateId(row.quill_member_id, 12)}
        </Typography>
      ),
    },
    {
      id: 'verdict',
      label: 'Verdict',
      render: (row) => {
        const c = verdictColor(row.verdict);
        return (
          <Chip
            label={verdictLabel(row.verdict)}
            size="small"
            sx={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}`, fontWeight: 600 }}
          />
        );
      },
    },
    { id: 'category', label: 'Category', render: (row) => row.category || '—' },
    {
      id: 'match_type',
      label: 'Match Type',
      render: (row) => <Chip label={matchTypeLabel(row.match_type)} size="small" variant="outlined" />,
    },
    {
      id: 'classifier',
      label: 'Classifier',
      render: (row) =>
        row.classifier_confidence != null || row.classifier_latency_ms != null ? (
          <Typography variant="caption">
            {row.classifier_confidence != null ? `${(row.classifier_confidence * 100).toFixed(0)}% conf` : ''}
            {row.classifier_confidence != null && row.classifier_latency_ms != null ? ' · ' : ''}
            {row.classifier_latency_ms != null ? `${row.classifier_latency_ms}ms` : ''}
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        ),
    },
  ];

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Privacy: only a SHA-256 hash of the member's query is stored — raw query text never appears here.
      </Alert>

      <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField size="small" fullWidth label="Member ID" value={memberId} onChange={(e) => setMemberId(e.target.value)} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <Select size="small" fullWidth displayEmpty value={verdict} onChange={(e) => setVerdict(e.target.value)}>
            <MenuItem value="">Any verdict</MenuItem>
            {DECISION_VERDICTS.map((v) => (
              <MenuItem key={v} value={v}>
                {verdictLabel(v)}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField size="small" fullWidth label="Category" value={category} onChange={(e) => setCategory(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TimeRangeFilter value={range} onChange={changeRange} />
        </Grid>
        <Grid item xs={12} sm={1}>
          <Button fullWidth variant="contained" onClick={runFilter} sx={{ backgroundColor: colors.deepTeal[500], '&:hover': { backgroundColor: colors.deepTeal[600] } }}>
            Filter
          </Button>
        </Grid>
      </Grid>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : (
        <>
          <DataTable<GuardrailDecision> columns={columns} data={decisions ?? []} loading={loading} emptyMessage="No decisions logged for this filter" />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Select size="small" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              {[25, 50, 100, 200].map((n) => (
                <MenuItem key={n} value={n}>
                  {n} per page
                </MenuItem>
              ))}
            </Select>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Offset {offset}
              </Typography>
              <Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
                Prev
              </Button>
              <Button size="small" disabled={(decisions?.length ?? 0) < limit} onClick={() => setOffset(offset + limit)}>
                Next
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DecisionsTab;
